import { useTranslation } from 'react-i18next';
import type { EpisodeSummary } from '@/types/streaming';
import { EpisodeTile } from './EpisodeTile';

export type SeasonStatus = 'idle' | 'loading' | 'error' | 'ready';

interface Props {
  tmdbId: number;
  seasonNumber: number;
  name: string;
  episodeCount: number;
  isOpen: boolean;
  status: SeasonStatus;
  episodes: EpisodeSummary[] | null;
  /** Episode number to highlight in this season, or null if the user isn't on this season. */
  currentEpisode: number | null;
  onToggle: () => void;
  onRetry: () => void;
}

export function SeasonRow({
  tmdbId,
  seasonNumber,
  name,
  episodeCount,
  isOpen,
  status,
  episodes,
  currentEpisode,
  onToggle,
  onRetry,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const sectionCls = `episode-picker__season${isOpen ? ' episode-picker__season--open' : ''}`;
  const headerLabel = name || t('episodes.seasonFallback', { number: seasonNumber });
  return (
    <section className={sectionCls}>
      <button
        type="button"
        className="episode-picker__season-header"
        onClick={onToggle}
        aria-expanded={isOpen}
        data-testid={`season-header-${seasonNumber}`}
      >
        <span className="episode-picker__season-name">{headerLabel}</span>
        <span className="episode-picker__season-count">{t('episodes.count', { count: episodeCount, formattedCount: String(episodeCount) })}</span>
      </button>
      {isOpen && (
        <div className="episode-picker__season-body">
          {status === 'loading' && (
            <div className="episode-picker__loading" data-testid={`season-loading-${seasonNumber}`}>
              {t('common.loading')}
            </div>
          )}
          {status === 'error' && (
            <div className="episode-picker__error" role="alert">
              <span>{t('episodes.couldntLoad')}</span>
              <button
                type="button"
                className="btn-ghost"
                onClick={onRetry}
                data-testid={`season-retry-${seasonNumber}`}
              >
                {t('common.retry')}
              </button>
            </div>
          )}
          {episodes && episodes.length > 0 && (
            <div className="episode-picker__grid">
              {episodes.map((ep) => (
                <EpisodeTile
                  key={`${ep.seasonNumber}-${ep.episodeNumber}`}
                  tmdbId={tmdbId}
                  seasonNumber={ep.seasonNumber}
                  episodeNumber={ep.episodeNumber}
                  name={ep.name}
                  stillPath={ep.stillPath}
                  isCurrent={ep.episodeNumber === currentEpisode}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
